import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import Loader from './ui/Loader';

export default function FeatureTable({ getLabel, features, plans }) {
	const incrementIncludedCount = (currentCount) => currentCount + 1;

	const formatInclusionAriaLabel = (featureName, planName, isIncluded) => {
		const labelKey = isIncluded ? 'plan_feature_included' : 'plan_feature_not_included';

		return getLabel(labelKey).replace('%1$s', featureName).replace('%2$s', planName);
	};

	const renderFeatureCell = (planId, featureName, cellData, isHeader = false) => {
		const Tag = isHeader ? 'th' : 'td';
		const cellProps = {
			className: 'prc-feature-table__content',
			...(isHeader && { scope: 'col' }),
			...(cellData.highlight && { 'data-color-scheme': 'highlight' }),
		};

		if (typeof cellData.content === 'boolean') {
			const isIncluded = cellData.content;
			const ariaLabel = formatInclusionAriaLabel(featureName, plans[planId].name, isIncluded);

			return (
				<Tag key={planId} {...cellProps}>
					<span data-check={isIncluded ? 1 : 0} aria-label={ariaLabel}>
						{isIncluded ? 'check' : 'close'}
					</span>
				</Tag>
			);
		}

		return <Tag key={planId} {...cellProps} dangerouslySetInnerHTML={{ __html: cellData.content }} />;
	};

	const renderFeatureTableGroups = (groups, selectedPlanId = '') => {
		return Object.keys(groups).map((groupId) => {
			const includedCountByPlan = {};
			const totalFeatures = features[groupId].features.length;
			const group = groups[groupId];
			const planColumns = !selectedPlanId ? group.cols : group.cols[selectedPlanId];

			if (selectedPlanId && !includedCountByPlan[selectedPlanId]) {
				includedCountByPlan[selectedPlanId] = 0;
			}

			const featureRows = Object.entries(group.features).map(([featureId, feature]) => {
				let shouldCount = true;
				if (selectedPlanId in feature.cols && 'count' in feature.cols[selectedPlanId]) {
					shouldCount = feature.cols[selectedPlanId].count;
				}

				if ((selectedPlanId && shouldCount) || (selectedPlanId in feature.cols && !('count' in feature.cols[selectedPlanId]))) {
					includedCountByPlan[selectedPlanId] = feature.cols[selectedPlanId].content
						? incrementIncludedCount(includedCountByPlan[selectedPlanId])
						: includedCountByPlan[selectedPlanId];
				}

				return (
					<tr key={featureId} className="prc-feature-table__item" tabIndex={0}>
						<th scope="row" className="prc-feature-table__name" dangerouslySetInnerHTML={{ __html: feature.name }} />
						{selectedPlanId
							? renderFeatureCell(selectedPlanId, feature.name, feature.cols[selectedPlanId])
							: Object.keys(feature.cols).map((planId) => {
									if (!includedCountByPlan[planId]) {
										includedCountByPlan[planId] = 0;
									}

									if ('count' in feature.cols[planId]) {
										shouldCount = feature.cols[planId].count;
									}

									if (shouldCount) {
										includedCountByPlan[planId] = feature.cols[planId].content
											? incrementIncludedCount(includedCountByPlan[planId])
											: includedCountByPlan[planId];
									}

									return renderFeatureCell(planId, feature.name, feature.cols[planId]);
								})}
					</tr>
				);
			});

			return (
				<tbody id={`prc-features-${groupId}`} key={groupId}>
					<tr className="prc-feature-table__group-header">
						<th scope="row" className="prc-feature-table__name" dangerouslySetInnerHTML={{ __html: !group.hide ? group.name : '' }} />
						{selectedPlanId
							? renderFeatureCell(selectedPlanId, group.name, {
									...group.cols[selectedPlanId],
									content: `${includedCountByPlan[selectedPlanId]} / ${totalFeatures}`,
								})
							: Object.keys(planColumns).map((planId) => {
									const content = { ...planColumns[planId], content: `${includedCountByPlan[planId]} / ${totalFeatures}` };
									return renderFeatureCell(planId, group.name, content);
								})}
					</tr>
					{featureRows}
				</tbody>
			);
		});
	};

	const renderPlanHeaderCell = (plan) => {
		return (
			<th key={plan.name} className="prc-feature-table__content" data-color-scheme={plan.most_popular ? 'highlight' : ''} scope="col">
				<h4>{plan.name}</h4>
			</th>
		);
	};

	const renderFeatureTable = () => {
		return (
			<div className="prc-feature-table__ctr">
				<table className="prc-feature-table">
					<thead>
						<tr className="prc-feature-table__item prc-feature-table__item--plans">
							<th scope="col" className="prc-feature-table__name" aria-hidden="true"></th>
							{Object.values(plans).map((plan) => {
								return renderPlanHeaderCell(plan);
							})}
						</tr>
					</thead>
					{renderFeatureTableGroups(features)}
				</table>
			</div>
		);
	};

	const domNode = document.getElementById('pricing-features');
	if (!domNode) {
		return null;
	}

	const children = !features || !plans ? <Loader /> : renderFeatureTable();
	return ReactDOM.createPortal(children, domNode);
}

FeatureTable.propTypes = {
	getLabel: PropTypes.func.isRequired,
	features: PropTypes.object,
	plans: PropTypes.object,
};
